import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { isGlobalAdmin } from "../utils/permissions";
import UserMenu from "../components/UserMenu";

export default function Projects() {
  const [projects, setProjects] = useState<any[]>([]);
  const [selected, setSelected] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const name = localStorage.getItem("name") || "User";
  const initials = name.split(" ").map((n: string) => n[0]).join("").toUpperCase();

  useEffect(() => {
    api.get("/api/projects").then((r) => {
      setProjects(r.data);
      if (r.data.length > 0) setSelected(r.data[0].id);
    });
  }, []);

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    p.bp_code.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status: string) => {
    if (status === "active") return "bg-green-50 text-green-600 border border-green-200";
    if (status === "inactive") return "bg-gray-100 text-gray-500 border border-gray-200";
    if (status === "maintenance") return "bg-orange-50 text-orange-500 border border-orange-200";
    return "bg-gray-100 text-gray-500";
  };

  const cpiColor = (cpi: number) => {
    if (!cpi) return "text-gray-400";
    if (cpi < 0.9) return "text-red-500";
    if (cpi < 0.95) return "text-amber-500";
    return "text-green-600";
  };

  const steps = ["Project", "Upload files", "Sanity Checks", "Configure", "Review & generate"];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">A</span>
          </div>
          <div>
            <div className="text-xs text-gray-400">M170 BP884</div>
            <div className="text-sm font-semibold text-gray-900 leading-tight">EAC Reporting</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => navigate("/dashboard/" + (localStorage.getItem("active_project_id") || ""))} className="px-4 py-1.5 text-gray-600 text-sm hover:text-gray-900">Dashboard</button>
          <button onClick={() => navigate("/reports")} className="px-4 py-1.5 text-gray-600 text-sm">Report</button>
          <button onClick={() => navigate("/reports")} className="px-4 py-1.5 text-gray-600 text-sm">History</button>
          {isGlobalAdmin() && (
            <button onClick={() => navigate("/users")} className="px-4 py-1.5 text-gray-600 text-sm">User Management</button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 flex items-center justify-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </button>
          <button className="w-8 h-8 flex items-center justify-center text-gray-500 relative">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <UserMenu />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SELECT A PROJECT</h1>
            <p className="text-xs text-gray-400 mt-0.5">CHOOSE WHICH PROGRAM THIS QUARTERLY EAC REPORT BELONGS TO.</p>
          </div>
          {/* Search bar */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search programs by name or code..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>
        </div>

        {/* Step bar */}
        <div className="flex items-center gap-0 mb-8 mt-6">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                  ${i === 0 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                  {i + 1}
                </div>
                <span className={`text-sm ${i === 0 ? "text-blue-600 font-medium" : "text-gray-400"}`}>{s}</span>
              </div>
              {i < 4 && <div className="w-8 h-px bg-gray-300 mx-2"></div>}
            </div>
          ))}
        </div>

        {/* Project cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {filtered.map((p: any) => (
            <div key={p.id} onClick={() => setSelected(p.id)}
              className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all
                ${selected === p.id ? "border-blue-600" : "border-gray-200 hover:border-gray-300"}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="w-8 h-8 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusBadge(p.status)}`}>
                  {p.status?.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-0.5">{p.code} {p.bp_code}</p>
              <p className="text-sm font-semibold text-gray-900 leading-tight mb-2">{p.name}</p>
              {p.last_report_date && (
                <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Last report {new Date(p.last_report_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              {p.latest_cpi && (
                <div className={`text-xs font-medium ${cpiColor(p.latest_cpi)} mb-3`}>
                  ● Latest CPI {p.latest_cpi?.toFixed(2)} · +${(Math.abs(p.latest_vac || 0) / 1000000).toFixed(1)}M VAC
                </div>
              )}
              {selected === p.id ? (
                <div className="w-full text-center py-1.5 border border-blue-200 rounded-lg text-blue-600 text-xs font-medium">
                  ✓ Selected
                </div>
              ) : (
                <div className="w-full text-center py-1.5 border border-gray-200 rounded-lg text-gray-500 text-xs">
                  Select
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button onClick={() => navigate(`/upload/${selected}`)} disabled={!selected}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            Continue Report →
          </button>
        </div>
      </div>
    </div>
  );
}
